/**
 * Dealer Areas / Territories — manage page.
 *
 * Mock-aware CRUD plus an "Assign dealers" multi-select action that lives
 * in the detail Sheet. Mutations all go through `auditedMutation` on the
 * server side; in mock mode they patch local React state and surface
 * toasts.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Map as MapIcon, Pencil, Plus, Trash2, Undo2, UserPlus } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  DealerAreaForm,
  type DealerAreaFormValues,
} from "../../../components/forms/DealerAreaForm";
import { type MockDealerArea } from "../../../lib/dealerAreasMock";
import { mockDealers, type MockDealer } from "../../../lib/salesMock";
import { mockBranches } from "../../../lib/inventoryMock";
import { useAppSettings, type BranchRow } from "../../../lib/appSettings";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/settings/dealer-areas")({
  component: DealerAreasPage,
});

/* ─────────────────────────────────────────────────────────────
 * Local types
 * ───────────────────────────────────────────────────────────── */

type AreaRow = MockDealerArea;

type DealerLite = {
  _id: string;
  code: string;
  displayName: string;
  dealerAreaId: string | null;
};

type StatusFilter = "all" | "active" | "archived";

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function DealerAreasPage() {
  const { dealerAreas: areas, updateDealerAreas } = useAppSettings();

  // Branches — load dynamically via usePersistentMockState so that any
  // branch created on the Settings > Branches page is immediately
  // available in the dropdown here without a page refresh. The seed uses
  // mockBranches (may be empty); demoStore / InstantDB provides real data
  // once the user has populated them.
  const [contextBranches] = usePersistentMockState<BranchRow[]>(
    "branches",
    mockBranches.map((b) => ({ ...b, deletedAt: null })),
  );

  // Dealers — persistent across navigation via usePersistentMockState so
  // the "assign dealers" mutations survive route changes and flow through
  // to InstantDB when configured.
  const [dealersFull, setDealersFull] = usePersistentMockState<MockDealer[]>(
    "dealers",
    mockDealers,
  );
  const dealers = useMemo<DealerLite[]>(
    () =>
      dealersFull.map((d) => ({
        _id: d._id,
        code: d.code,
        displayName: d.displayName,
        dealerAreaId: d.dealerAreaId ?? null,
      })),
    [dealersFull],
  );

  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<AreaRow | null>(null);
  const [detailRow, setDetailRow] = useState<AreaRow | null>(null);
  const [assignSheetOpen, setAssignSheetOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<AreaRow | null>(null);

  const branchById = useMemo(
    () => new Map(contextBranches.map((b) => [b._id, b])),
    [contextBranches],
  );

  const dealerCountByArea = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of dealers) {
      if (d.dealerAreaId) {
        map.set(d.dealerAreaId, (map.get(d.dealerAreaId) ?? 0) + 1);
      }
    }
    return map;
  }, [dealers]);

  const filtered = useMemo(() => {
    return areas.filter((a) => {
      if (branchFilter !== "all" && a.branchId !== branchFilter) return false;
      if (statusFilter === "active" && a.deletedAt !== null) return false;
      if (statusFilter === "archived" && a.deletedAt === null) return false;
      return true;
    });
  }, [areas, branchFilter, statusFilter]);

  const kpis = useMemo(() => {
    const live = areas.filter((a) => a.deletedAt === null);
    const total = live.length;
    const active = live.filter((a) => a.isActive).length;
    const areasWithDealers = Array.from(dealerCountByArea.keys()).filter(
      (id) => live.some((a) => a._id === id),
    ).length;
    let largestCount = 0;
    let largestName = "—";
    for (const a of live) {
      const c = dealerCountByArea.get(a._id) ?? 0;
      if (c > largestCount) {
        largestCount = c;
        largestName = a.displayName;
      }
    }
    return {
      total,
      active,
      areasWithDealers,
      largestName,
      largestCount,
    };
  }, [areas, dealerCountByArea]);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: AreaRow) {
    setEditing(row);
    setDetailRow(null);
    setSheetOpen(true);
  }

  function handleSubmit(values: DealerAreaFormValues) {
    const now = Date.now();
    if (editing) {
      updateDealerAreas((prev) =>
        prev.map((a) =>
          a._id === editing._id
            ? {
                ...a,
                displayName: values.displayName,
                branchId: values.branchId,
                district: values.district,
                description: values.description,
                isActive: values.isActive,
                updatedAt: now,
              }
            : a,
        ),
      );
      toast.success("Area updated. Audit trail recorded.");
    } else {
      const newId = `area_${Date.now()}`;
      updateDealerAreas((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          branchId: values.branchId,
          district: values.district,
          description: values.description,
          isActive: values.isActive,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
        },
      ]);
      toast.success("Area created (mock)");
    }
    setSheetOpen(false);
  }

  function handleArchive(row: AreaRow) {
    setArchiveTarget(row);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    updateDealerAreas((prev) =>
      prev.map((a) =>
        a._id === archiveTarget._id
          ? { ...a, isActive: false, deletedAt: Date.now() }
          : a,
      ),
    );
    setDetailRow(null);
    setArchiveTarget(null);
    toast.success("Area archived");
  }

  function handleRestore(row: AreaRow) {
    updateDealerAreas((prev) =>
      prev.map((a) =>
        a._id === row._id ? { ...a, isActive: true, deletedAt: null } : a,
      ),
    );
    setDetailRow(null);
    toast.success("Area restored");
  }

  function handleAssignDealers(areaId: string, dealerIds: string[]) {
    const incoming = new Set(dealerIds);
    setDealersFull((prev) =>
      prev.map((d) => {
        if (incoming.has(d._id)) return { ...d, dealerAreaId: areaId };
        if (d.dealerAreaId === areaId) return { ...d, dealerAreaId: null };
        return d;
      }),
    );
    setAssignSheetOpen(false);
    toast.success(`Assigned ${dealerIds.length} dealers to area`);
  }

  const columns = useMemo<DataTableColumn<AreaRow>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "district",
        accessorKey: "district",
        header: "District",
        cell: (info) => (
          <span className="text-(--text-secondary)">
            {info.row.original.district}
          </span>
        ),
      },
      {
        id: "branch",
        header: "Managing branch",
        cell: (info) => {
          const b = branchById.get(info.row.original.branchId);
          return (
            <span className="text-(--text-secondary)">
              {b?.displayName ?? "—"}
            </span>
          );
        },
      },
      {
        id: "dealersCount",
        header: "Dealers",
        cell: (info) => (
          <Badge variant="info" size="sm">
            {dealerCountByArea.get(info.row.original._id) ?? 0}
          </Badge>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => {
          const r = info.row.original;
          if (r.deletedAt !== null) {
            return (
              <Badge variant="neutral" size="sm">
                Archived
              </Badge>
            );
          }
          return (
            <Badge variant={r.isActive ? "success" : "neutral"} size="sm">
              {r.isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
    ],
    [branchById, dealerCountByArea],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dealer Areas / Territories"
        description="Sales territories — group dealers under a managing branch. Assignment changes are audited so the territory map stays trustworthy."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add area
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard caption="Total areas" value={kpis.total.toString()} />
        <KpiCard caption="Active" value={kpis.active.toString()} />
        <KpiCard
          caption="Areas with dealers"
          value={kpis.areasWithDealers.toString()}
        />
        <KpiCard
          caption="Largest area"
          value={kpis.largestName}
          hint={`${kpis.largestCount} dealers`}
        />
      </div>

      <FilterBar
        branchFilter={branchFilter}
        onBranchChange={setBranchFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        contextBranches={contextBranches}
      />

      <DataTable
        data={filtered}
        columns={columns}
        emptyTitle="No areas match"
        emptyDescription="Adjust filters or create a new area."
        emptyIcon={<MapIcon className="size-5 text-(--text-tertiary)" />}
        onRowClick={(row) => setDetailRow(row.original)}
      />

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent size="md">
          {detailRow ? (
            <AreaDetail
              area={detailRow}
              branchName={
                branchById.get(detailRow.branchId)?.displayName ?? "—"
              }
              dealersInArea={dealers.filter(
                (d) => d.dealerAreaId === detailRow._id,
              )}
              onEdit={() => openEdit(detailRow)}
              onArchive={() => handleArchive(detailRow)}
              onRestore={() => handleRestore(detailRow)}
              onAssign={() => setAssignSheetOpen(true)}
              onClose={() => setDetailRow(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Assign-dealers Sheet */}
      <Sheet open={assignSheetOpen} onOpenChange={setAssignSheetOpen}>
        <SheetContent size="md">
          {detailRow ? (
            <AssignDealersForm
              area={detailRow}
              allDealers={dealers}
              onCancel={() => setAssignSheetOpen(false)}
              onSubmit={(ids) => handleAssignDealers(detailRow._id, ids)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit area" : "New area"}</SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the area details. Code is locked once created."
                : "Areas group dealers under a managing branch — pick a stable, descriptive code."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <DealerAreaForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              branches={contextBranches
                .filter((b) => b.deletedAt === null && b.isActive)
                .map((b) => ({
                  id: b._id,
                  displayName: b.displayName,
                }))}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      branchId: editing.branchId,
                      district: editing.district,
                      description: editing.description,
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Archive warning Dialog */}
      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              Archive {archiveTarget?.displayName}?
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const dealersCount = archiveTarget
                  ? (dealerCountByArea.get(archiveTarget._id) ?? 0)
                  : 0;
                return (
                  <>
                    {dealersCount > 0 ? (
                      <span className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="size-4 shrink-0 mt-0.5 text-(--warning)" />
                        <span>
                          {dealersCount} dealer(s) are currently assigned to
                          this area. They will become unassigned.
                        </span>
                      </span>
                    ) : null}
                    This area will be hidden from new records.
                  </>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmArchive}>
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Filter chips
 * ───────────────────────────────────────────────────────────── */

function FilterBar({
  branchFilter,
  onBranchChange,
  statusFilter,
  onStatusChange,
  contextBranches,
}: {
  branchFilter: string;
  onBranchChange: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusChange: (v: StatusFilter) => void;
  contextBranches: ReturnType<typeof useAppSettings>["branches"];
}) {
  const branchOptions = [
    { value: "all", label: "All branches" },
    ...contextBranches
      .filter((b) => b.deletedAt === null && b.isActive)
      .map((b) => ({ value: b._id, label: b.displayName })),
  ];
  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mr-1">
          Branch
        </span>
        {branchOptions.map((opt) => (
          <Chip
            key={opt.value}
            active={branchFilter === opt.value}
            onClick={() => onBranchChange(opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mr-1">
          Status
        </span>
        {statusOptions.map((opt) => (
          <Chip
            key={opt.value}
            active={statusFilter === opt.value}
            onClick={() => onStatusChange(opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 h-7 text-[12px] transition-colors",
        active
          ? "bg-(--bg-selected) border-(--accent) text-(--accent)"
          : "bg-(--bg-canvas) border-(--border-subtle) text-(--text-secondary) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function AreaDetail({
  area,
  branchName,
  dealersInArea,
  onEdit,
  onArchive,
  onRestore,
  onAssign,
  onClose,
}: {
  area: AreaRow;
  branchName: string;
  dealersInArea: DealerLite[];
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onAssign: () => void;
  onClose: () => void;
}) {
  const isArchived = area.deletedAt !== null;
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{area.code}</span>
          {isArchived ? (
            <Badge variant="neutral" size="sm">
              Archived
            </Badge>
          ) : (
            <Badge variant={area.isActive ? "success" : "neutral"} size="sm">
              {area.isActive ? "Active" : "Inactive"}
            </Badge>
          )}
          <RecordAuditBadge tableName="dealerAreas" recordId={area._id} />
        </SheetTitle>
        <SheetDescription>
          {area.displayName} · {area.district}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="dealers">
              Dealers ({dealersInArea.length})
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              <section className="grid grid-cols-2 gap-3">
                <DetailField label="Code" value={area.code} />
                <DetailField label="District" value={area.district} />
                <DetailField label="Managing branch" value={branchName} />
                <DetailField
                  label="Dealers"
                  value={dealersInArea.length.toString()}
                />
              </section>
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Description
                </div>
                <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3 text-[13px] text-(--text-primary)">
                  {area.description || "—"}
                </div>
              </section>
            </div>
          </TabsContent>
          <TabsContent value="dealers" className="mt-4">
            <div className="flex justify-end mb-2">
              <Button variant="secondary" size="sm" onClick={onAssign}>
                <UserPlus className="size-3.5" /> Assign dealers
              </Button>
            </div>
            {dealersInArea.length === 0 ? (
              <div className="rounded-md border border-dashed border-(--border-subtle) p-4 text-[12px] text-(--text-tertiary) text-center">
                No dealers in this area yet. Click Assign dealers to add some.
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {dealersInArea.map((d) => (
                  <li
                    key={d._id}
                    className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2"
                  >
                    <div>
                      <div className="font-mono text-[12px] text-(--text-primary)">
                        {d.code}
                      </div>
                      <div className="text-[12px] text-(--text-secondary)">
                        {d.displayName}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="dealerAreas" recordId={area._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {isArchived ? (
          <Button variant="secondary" onClick={onRestore}>
            <Undo2 className="size-3.5" /> Restore
          </Button>
        ) : (
          <Button variant="secondary" onClick={onArchive}>
            <Trash2 className="size-3.5" /> Archive
          </Button>
        )}
        <Button size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Assign-dealers form
 * ───────────────────────────────────────────────────────────── */

function AssignDealersForm({
  area,
  allDealers,
  onSubmit,
  onCancel,
}: {
  area: AreaRow;
  allDealers: DealerLite[];
  onSubmit: (dealerIds: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        allDealers
          .filter((d) => d.dealerAreaId === area._id)
          .map((d) => d._id),
      ),
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Assign dealers · {area.displayName}</SheetTitle>
        <SheetDescription>
          Toggle dealers in or out of this area. Dealers in another area will
          be moved here.
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <ul className="flex flex-col gap-1">
          {allDealers.map((d) => {
            const isHere = selected.has(d._id);
            const elsewhere =
              !isHere && d.dealerAreaId && d.dealerAreaId !== area._id;
            return (
              <li key={d._id}>
                <button
                  type="button"
                  onClick={() => toggle(d._id)}
                  className={cn(
                    "w-full flex items-center justify-between rounded-md border px-3 py-2 transition-colors text-left",
                    isHere
                      ? "border-(--accent) bg-(--bg-selected)"
                      : "border-(--border-subtle) bg-(--bg-canvas) hover:bg-(--bg-hover)",
                  )}
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-[12px] text-(--text-primary)">
                      {d.code}
                    </span>
                    <span className="text-[12px] text-(--text-secondary)">
                      {d.displayName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {elsewhere ? (
                      <Badge variant="warning" size="sm">
                        Will move
                      </Badge>
                    ) : null}
                    {isHere ? (
                      <Badge variant="default" size="sm">
                        Selected
                      </Badge>
                    ) : null}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(Array.from(selected))}>
          Save assignments ({selected.size})
        </Button>
      </SheetFooter>
    </>
  );
}
